import {
  collection,
  addDoc,
  getDocs,
  doc,
  query,
  orderBy,
  runTransaction,
  updateDoc,
  getDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';
import { deductStockFIFO } from './stockBatchService';
import { getInventoryItems, addInventoryItem, updateInventoryItem } from './inventoryService';

const COLLECTION = 'decantTransfers';

// Auto-generate Decant Transfer number: DCT-001, DCT-002, ...
export const generateDecantTransferNumber = async () => {
  try {
    const counterRef = doc(db, 'counters', 'decantTransferNumber');
    const result = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      const next = counterDoc.exists() ? (counterDoc.data().current || 0) + 1 : 1;
      transaction.set(counterRef, { current: next }, { merge: true });
      return `DCT-${String(next).padStart(3, '0')}`;
    });
    return { success: true, transferNumber: result };
  } catch (error) {
    return { success: true, transferNumber: `DCT-${Date.now()}` };
  }
};

// Get all decant transfers
export const getDecantTransfers = async () => {
  try {
    const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return {
      success: true,
      data: snapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
    };
  } catch (error) {
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Create a decant transfer (simplified ML-based approach).
 *
 * Each line:
 *   - Deducts source perfume bottle(s) from inventory
 *   - Creates/updates a decant product tracked in ML units
 *   - Each ML unit = 1ml of perfume
 *   - decantPrices: { 5: price5ml, 10: price10ml } stored on the inventory item
 *
 * transferData = {
 *   transferNumber, date, notes, createdBy,
 *   lines: [{
 *     sourceItemId,     // perfume bottle inventory ID
 *     sourceItemName,   // e.g. "Yara EDP 100ml"
 *     sourceQty,        // bottles to use (e.g. 1)
 *     sourceBottleML,   // ML per bottle (e.g. 100)
 *     totalML,          // total ML produced = sourceQty × sourceBottleML
 *     outputItemName,   // e.g. "Yara EDP (Decant)"
 *     outputItemId,     // existing inventory ID or null
 *     outputCategory,   // "Decants"
 *     costPerML,        // cost per 1ml unit
 *     price5ml,         // selling price for 5ml
 *     price10ml,        // selling price for 10ml
 *   }]
 * }
 */
export const createDecantTransfer = async (transferData) => {
  try {
    const { transferNumber, date, notes, createdBy, lines } = transferData;

    const invResult = await getInventoryItems();
    const currentInventory = invResult.success ? invResult.data : [];

    const processedLines = await Promise.all(
      lines.map(async (line) => {
        // 1. Deduct source perfume bottle(s) via FIFO
        const sourceDeduct = await deductStockFIFO(line.sourceItemId, line.sourceQty);
        if (!sourceDeduct.success) {
          throw new Error(`Failed to deduct source stock for "${line.sourceItemName}": ${sourceDeduct.error}`);
        }

        // 2. Find or create the decant output inventory item
        let outputItem = line.outputItemId
          ? currentInventory.find((i) => i.id === line.outputItemId)
          : currentInventory.find(
              (i) => i.name.toLowerCase() === line.outputItemName.toLowerCase()
            );

        const decantPrices = {
          5:  { retail: parseFloat(line.price5ml)  || 0, wholesale: parseFloat(line.price5mlWholesale)  || parseFloat(line.price5ml)  || 0 },
          10: { retail: parseFloat(line.price10ml) || 0, wholesale: parseFloat(line.price10mlWholesale) || parseFloat(line.price10ml) || 0 },
        };

        if (!outputItem) {
          // Create new decant inventory item — stock tracked in ML
          const addResult = await addInventoryItem({
            name: line.outputItemName,
            category: line.outputCategory || 'Decants',
            stock: 0,
            costPrice: line.costPerML || 0,
            retailPrice: decantPrices[5]?.retail || 0,
            wholesalePrice: decantPrices[5]?.wholesale || 0,
            itemCode: line.outputItemCode || '',
            isDecant: true,
            trackInML: true,
            decantPrices,
            batches: [],
          });
          if (!addResult.success) {
            throw new Error(`Failed to create decant inventory item: ${line.outputItemName}`);
          }
          outputItem = { id: addResult.id, name: line.outputItemName };
        } else {
          // Update prices on existing item AND ensure category is set
          await updateInventoryItem(outputItem.id, { 
            decantPrices,
            category: line.outputCategory || 'Decants', // Ensure category is set for existing items
          });
        }

        // 3. Add ML units directly to inventory stock (no FIFO batch needed for ML tracking)
        const itemRef = doc(db, 'inventory', outputItem.id);
        const itemSnap = await getDoc(itemRef);
        if (itemSnap.exists()) {
          const currentStock = itemSnap.data().stock || 0;
          await updateDoc(itemRef, {
            stock: currentStock + line.totalML,
            costPrice: line.costPerML || 0,
            decantPrices,
            category: line.outputCategory || 'Decants', // Ensure category is always set
            updatedAt: serverTimestamp(),
          });
        }

        return {
          sourceItemId: line.sourceItemId,
          sourceItemName: line.sourceItemName,
          sourceQty: line.sourceQty,
          sourceBottleML: line.sourceBottleML,
          totalML: line.totalML,
          outputItemId: outputItem.id,
          outputItemName: line.outputItemName,
          costPerML: line.costPerML || 0,
          price5ml: decantPrices[5]?.retail,
          price10ml: decantPrices[10]?.retail,
        };
      })
    );

    // Save the decant transfer record
    const totalML = lines.reduce((s, l) => s + l.totalML, 0);
    const docRef = await addDoc(collection(db, COLLECTION), {
      transferNumber,
      date,
      notes: notes || '',
      createdBy: createdBy || '',
      lines: processedLines,
      totalML,
      createdAt: new Date().toISOString(),
    });

    return {
      success: true,
      id: docRef.id,
      transferNumber,
      processedLines,
      totalML,
    };
  } catch (error) {
    console.error('Error creating decant transfer:', error);
    return { success: false, error: error.message };
  }
};
